import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Spinner } from "./Spinner";

const variantStyles = {
	primary: "btn-gradient text-white shadow-sm hover:shadow-md",
	secondary:
		"bg-secondary text-white hover:bg-secondary-dark transition-colors shadow-sm hover:shadow-md",
	outline:
		"border-2 border-primary text-primary bg-transparent hover:bg-primary/5 transition-colors",
	ghost: "text-text-secondary bg-transparent hover:bg-surface-dim transition-colors",
	danger: "bg-error text-white hover:brightness-110 transition-all shadow-sm",
} as const;

const sizeStyles = {
	sm: "px-3 py-1.5 text-sm",
	md: "px-4 py-2 text-base",
	lg: "px-6 py-3 text-lg",
} as const;

const spinnerSizeMap = {
	sm: "sm",
	md: "sm",
	lg: "md",
} as const;

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: keyof typeof variantStyles;
	size?: keyof typeof sizeStyles;
	loading?: boolean;
	fullWidth?: boolean;
	leftIcon?: ReactNode;
	rightIcon?: ReactNode;
}

export function Button({
	variant = "primary",
	size = "md",
	loading = false,
	fullWidth = false,
	leftIcon,
	rightIcon,
	children,
	disabled,
	className = "",
	...props
}: ButtonProps) {
	const isDisabled = disabled || loading;

	return (
		<button
			type="button"
			disabled={isDisabled}
			aria-busy={loading || undefined}
			className={`
				inline-flex items-center justify-center gap-2 rounded-lg font-medium
				focus-ring cursor-pointer
				disabled:opacity-50 disabled:cursor-not-allowed
				${variantStyles[variant]}
				${sizeStyles[size]}
				${fullWidth ? "w-full" : ""}
				${className}
			`}
			{...props}
		>
			{loading ? (
				<Spinner size={spinnerSizeMap[size]} label="Bitte warten" />
			) : leftIcon ? (
				<span className="inline-flex shrink-0" aria-hidden="true">
					{leftIcon}
				</span>
			) : null}
			{children}
			{!loading && rightIcon && (
				<span className="inline-flex shrink-0" aria-hidden="true">
					{rightIcon}
				</span>
			)}
		</button>
	);
}
